import { Icon, IconName } from "components/icons/Icon";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { useSupportEmail } from "hooks/useSupportEmail";
import { useEventTracking } from "hooks/useEventTracking";
import { emergencyContacts, additionalResources, internationalResources } from "../../constants/resources";
import EmergencyImage from "assets/Images/Emergency.png";

const toAbsoluteUrl = (value: string) =>
    value.startsWith("http://") || value.startsWith("https://") ? value : `https://${value}`;

export const Resources: React.FC = () => {
  const supportEmail = useSupportEmail();
    const { track } = useEventTracking();

    const handleLinkClick = (url: string) => {
        track("button_click", { link: url });
    };

    return (
        <div className="relative flex min-h-screen w-full flex-col items-center">
          <div className="mt-5 flex w-full min-w-0 animate-fade-in flex-col items-start gap-10 opacity-0">
                <Card variant="glass" maxWidth="none" >
                    <CardContent
                        variant="flex-row"
                        padding="none"
                        className="w-full flex-col gap-6 md:flex-row md:gap-8"
                    >
                        <img
                            className="relative h-auto w-full max-w-[360px] self-center rounded-3xl object-cover md:self-auto"
                            alt="Emergency resources illustration"
                            src={EmergencyImage}
                        />

                        <div className="flex min-w-0 flex-1 flex-col items-start gap-6">
                            <div className="flex flex-col gap-1">
                                <CardHeader>
                                    MENTAL HEALTH CRISIS
                                </CardHeader>
                                <CardTitle 
                                title="Emergency Resources"
                                subtitleSize="extraSmall"
                                className="pb-0"
                                />
                            </div>
                            <p className="break-words type-body-m text-foundation-base-darkbase-dark-500">
                            If you or someone you know are experiencing mental health
                            crisis, it&apos;s important to seek immediate help. Here are
                            some emergency resources you can reach out if you or someone
                            you know is struggling.
                            </p>

                            <Card variant="default" maxWidth="none" padding="compact" disableAnimation className="p-4">
                            <CardContent variant="custom" padding="none">
                                <p className="flex-1 break-words type-body-m text-foundation-base-darkbase-dark-500">
                                Remember, reaching out for help is a sign of strength. These
                                resources are here to provide the support and assistance you
                                may need.
                                </p>
                            </CardContent>
                            </Card>
                        </div>
                    </CardContent>
                </Card>

                <section className="flex flex-col items-start gap-6 w-full translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
                    <h2 className="type-heading-h2 text-foundation-base-darkbase-dark-500">
                    Emergency Contacts
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                    {emergencyContacts.map((contact, index) => (
                        <Card
                        key={index}
                        variant="glass"
                        maxWidth="none"
                        padding="default"
                        className="flex flex-col items-start gap-6"
                        >
                        <CardContent variant="custom" padding="none" className="flex flex-col items-start gap-4 w-full">
                            <CardTitle title={contact.title} size="small" className="pb-0" />

                            <div className="flex flex-col items-start gap-4 w-full">
                            {contact.contacts.map((item, idx) => (
                                <div
                                key={idx}
                                className="flex flex-col items-start gap-2"
                                >
                                <p className="type-body-m font-semibold text-foundation-base-darkbase-dark-500">
                                    {item.type}
                                </p>
                                <div className="inline-flex items-center gap-2.5">
                                    <Icon name={item.icon as IconName} size={16}/>
                                    {item.type === "Website:" ? (
                                        <a
                                            href={toAbsoluteUrl(item.value)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="type-body-m-semibold text-foundation-base-darkbase-dark-500 hover:underline transition-all"
                                            onClick={() => handleLinkClick(toAbsoluteUrl(item.value))}
                                        >
                                            {item.value}
                                        </a>
                                        ) : (
                                        <span className="type-body-m-semibold text-foundation-base-darkbase-dark-500">
                                            {item.value}
                                        </span>
                                        )}
                                </div>
                                </div>
                            ))}
                            </div>
                        </CardContent>
                        </Card>
                    ))}
                    </div>
                </section>

                <section className="flex flex-col items-start gap-6 w-full translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                    <h2 className="type-heading-h2 text-foundation-base-darkbase-dark-500">
                    Additional Resources
                    </h2>

                    {additionalResources.map((row, rowIndex) => (
                    <div
                        key={rowIndex}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full"
                    >
                        {row.map((contact, index) => (
                        <Card
                            key={index}
                            variant="glass"
                            maxWidth="none"
                            padding="default"
                            className="flex flex-col items-start gap-6"
                        >
                            <CardContent variant="custom" padding="none" className="flex flex-col items-start gap-4 w-full">
                            <CardTitle title={contact.title} size="small" className="pb-0" />

                            <div className="flex flex-col items-start gap-4 w-full">
                                {contact.contacts.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex flex-col items-start gap-2"
                                >
                                    <p className="type-body-m font-semibold text-foundation-base-darkbase-dark-500">
                                    {item.type}
                                    </p>
                                    <div className="inline-flex items-center gap-2.5">
                                    <Icon name={item.icon as IconName} size={16}/>
                                    {item.type === "Website:" ? (
                                        <a
                                            href={toAbsoluteUrl(item.value)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="type-body-m-semibold text-foundation-base-darkbase-dark-500 hover:underline transition-all"
                                            onClick={() => handleLinkClick(toAbsoluteUrl(item.value))}
                                        >
                                            {item.value}
                                        </a>
                                        ) : (
                                        <span className="type-body-m-semibold text-foundation-base-darkbase-dark-500">
                                            {item.value}
                                        </span>
                                        )}
                                    </div>
                                </div>
                                ))}
                            </div>
                            </CardContent>
                        </Card>
                        ))}
                    </div>
                    ))}
                </section>

                <section className="flex flex-col items-start gap-6 w-full translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms]">
                    <h2 className="type-heading-h2 text-foundation-base-darkbase-dark-500">
                    International Resources
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                    {internationalResources.map((contact, index) => (
                        <Card
                        key={index}
                        variant="glass"
                        maxWidth="none"
                        padding="default"
                        className="flex flex-col items-start gap-6"
                        >
                        <CardContent variant="custom" padding="none" className="flex flex-col items-start gap-4 w-full">
                            <CardTitle title={contact.title} size="small" className="pb-0" />

                            <div className="flex flex-col items-start gap-4 w-full">
                            {contact.contacts.map((item, idx) => (
                                <div
                                key={idx}
                                className="flex flex-col items-start gap-2"
                                >
                                <p className="type-body-m font-semibold text-foundation-base-darkbase-dark-500">
                                    {item.type}
                                </p>
                                    <div className="inline-flex items-center gap-2.5">
                                    <Icon name={item.icon as IconName} size={16}/>
                                    {item.type === "Website:" ? (
                                        <a
                                            href={toAbsoluteUrl(item.value)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="type-body-m-semibold text-foundation-base-darkbase-dark-500 hover:underline transition-all"
                                            onClick={() => handleLinkClick(toAbsoluteUrl(item.value))}
                                        >
                                            {item.value}
                                        </a>
                                        ) : (
                                        <span className="type-body-m-semibold text-foundation-base-darkbase-dark-500">
                                            {item.value}
                                        </span>
                                        )}
                                </div>
                                </div>
                            ))}
                            </div>
                        </CardContent>
                        </Card>
                    ))}
                    </div>
                </section>

                <section className="flex flex-col items-start gap-6 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms]">
                    <h2 className="type-heading-h2 text-foundation-base-darkbase-dark-500">
                    Local Emergency Services
                    </h2>

                    <Card variant="glass" maxWidth="none" padding="default" className="flex flex-col items-start gap-6 w-full">
                    <CardContent variant="custom" padding="none" className="flex flex-col items-start gap-4 w-full">
                        <p className="type-body-m text-foundation-base-darkbase-dark-500">
                        If you are in immediate danger, call your local emergency
                        services number (eg. 911 in US, 999 in the UK, 112 in Europe)
                        </p>
                    </CardContent>
                    </Card>
                </section>

                <Card variant="glass" maxWidth="none" padding="default" className="flex flex-col items-start gap-6 w-full translate-y-[-1rem] [--animation-delay:1200ms]">
                    <CardContent variant="custom" padding="none" className="flex flex-col items-start gap-4 w-full">
                    <p className="type-body-m text-foundation-base-darkbase-dark-500">
                        For other inquiries unrelated to emergency services, email{" "}
                        <span className="type-body-m-semibold">
                            <a
                                href={`mailto:${supportEmail}`}
                                className="underline-offset-2 hover:underline"
                                onClick={() => {
                                    track("button_click", {
                                        action: "support_cta_click",
                                        control_id: "help_resources_support_email",
                                        support_target: "email",
                                        support_surface: "help_resources_page",
                                        destination: `mailto:${supportEmail}`,
                                    });
                                }}
                            >
                                {supportEmail}
                            </a>
                        </span>
                    </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
